import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { settingsAPI, mediaAPI } from '../../lib/api';
import { Loader2, Save, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '',
    tagline: '',
    logo_url: '',
    favicon_url: '',
    contact_email: '',
    social_twitter: '',
    social_instagram: '',
    social_tiktok: '',
    social_youtube: '',
    social_facebook: '',
    footer_text: '',
    analytics_enabled: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await settingsAPI.get();
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, checked) => {
    setSettings(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mediaAPI.upload(formData);
      setSettings(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-settings">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold">Site Settings</h1>
            <p className="text-muted-foreground">Configure your website settings</p>
          </div>
          <Button type="submit" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Basic site information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="site_name">Site Name</Label>
                <Input
                  id="site_name"
                  name="site_name"
                  value={settings.site_name}
                  onChange={handleChange}
                  placeholder="The Trade Media"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagline">Tagline</Label>
                <Input
                  id="tagline"
                  name="tagline"
                  value={settings.tagline}
                  onChange={handleChange}
                  placeholder="Entertainment News & Culture"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={settings.contact_email}
                  onChange={handleChange}
                  placeholder="info@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="footer_text">Footer Text</Label>
                <Textarea
                  id="footer_text"
                  name="footer_text"
                  value={settings.footer_text || ''}
                  onChange={handleChange}
                  placeholder="Additional footer text..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Logo and visual identity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Logo</Label>
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo" className="h-16 object-contain bg-secondary/50 rounded-sm p-2" />
                )}
                <div className="flex gap-2">
                  <Input
                    name="logo_url"
                    value={settings.logo_url || ''}
                    onChange={handleChange}
                    placeholder="Logo URL"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo_url')}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="icon" disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Favicon</Label>
                {settings.favicon_url && (
                  <img src={settings.favicon_url} alt="Favicon" className="h-8 w-8 object-contain" />
                )}
                <div className="flex gap-2">
                  <Input
                    name="favicon_url"
                    value={settings.favicon_url || ''}
                    onChange={handleChange}
                    placeholder="Favicon URL"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'favicon_url')}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="icon" disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media */}
          <Card>
            <CardHeader>
              <CardTitle>Social Media</CardTitle>
              <CardDescription>Social media profile links</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="social_twitter">Twitter/X</Label>
                <Input
                  id="social_twitter"
                  name="social_twitter"
                  value={settings.social_twitter || ''}
                  onChange={handleChange}
                  placeholder="https://twitter.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_instagram">Instagram</Label>
                <Input
                  id="social_instagram"
                  name="social_instagram"
                  value={settings.social_instagram || ''}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_tiktok">TikTok</Label>
                <Input
                  id="social_tiktok"
                  name="social_tiktok"
                  value={settings.social_tiktok || ''}
                  onChange={handleChange}
                  placeholder="https://tiktok.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_youtube">YouTube</Label>
                <Input
                  id="social_youtube"
                  name="social_youtube"
                  value={settings.social_youtube || ''}
                  onChange={handleChange}
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_facebook">Facebook</Label>
                <Input
                  id="social_facebook"
                  name="social_facebook"
                  value={settings.social_facebook || ''}
                  onChange={handleChange}
                  placeholder="https://facebook.com/..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <Card>
            <CardHeader>
              <CardTitle>Features</CardTitle>
              <CardDescription>Enable or disable features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="analytics_enabled">Analytics Tracking</Label>
                  <p className="text-sm text-muted-foreground">Track page views and visitor statistics</p>
                </div>
                <Switch
                  id="analytics_enabled"
                  checked={settings.analytics_enabled}
                  onCheckedChange={(checked) => handleSwitchChange('analytics_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminSettings;
